import { Injectable } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import moment, { Moment } from 'moment-timezone';
import { ITimesTemp } from '@wind-log_library/fe-component-library';

import { TranslateService } from '@ngx-translate/core';

import { NavigationLevel } from '../data-services/navigation/navigation.interface';
import { NavigationDetails, NavigationService, NavigationCategory } from '../data-services/navigation/navigation.service';
import { DimensionService } from 'app/analyze/service/dimension-service/dimension.service';
import { DragObjectInterface } from 'app/reporting/interfaces/drag-object-interface';
import { UserPreferencesService } from 'app/shared/services-api-central/user-preferences/user-preferences.service';
import { filter, take } from 'rxjs/operators';
import Keycloak from 'keycloak-js';

interface TimezoneOption  {
  label: string;
  value: string;
  isSpecial: boolean;
  divider: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class TimezoneService {
  public isVisible: boolean;
  public isDisabled = false;
  public level:NavigationLevel;
  public options: TimezoneOption[];
  
  private timezone: string;
  private timezone$ = new ReplaySubject<{timezone:string}>(1);
  private timezoneWithNavigation$ = new ReplaySubject<{timezone:string}>(1);
  private navigationDetails: NavigationDetails;
  private turbineTimezone: string;
  private userTimezone: string =  moment.tz.guess();
  private modulesWithTimezone = ['budget','tickets','monitor','overview','check_revenues','forecast','analyze','de_ice','data_studio','kpi-follow-up','planning','logbook','troubleshoot','revenues'];
  private parkTimezoneList: {_id: string, timezone: string, name: string, code: string}[] = [];
  private isFirstLoad: boolean = true;
  private parkId: string;
  private module: string;
  private disabledModules = ['kpi-follow-up', 'budget'];

  private availableTimezones = []
  private isFromReportService: boolean; // to avoid overwriting user timezone when coming from report service and impersonation from keycloak
 


  public getAllTimezones(): string[] {
    return this.availableTimezones.slice();
  }

  public get selectedOption(): string {
    if(!this.isVisible) return  undefined
    return this.timezone;
      
  } 

  constructor(
    private dimensionService: DimensionService,
    private userPreferencesService: UserPreferencesService,
    private navigationService: NavigationService,
    private translateService: TranslateService,
    private keycloakService: Keycloak
  ) {
    this.initService();
  }

  private async initService() {
    this.setIsFromReportService();
    await this.setAvailableTimezones();
    await this.setUserTimezone();
    this.updateTimezoneOnNavigation();
  }

  private updateTimezoneOnNavigation() {
    this.navigationService
      .getNavigationObservable([
        NavigationCategory.PortfolioLevelSelectedPortfolio,
        NavigationCategory.ParkLevelSelectedPark,
        NavigationCategory.TurbineLevelSelectedTurbine,
        NavigationCategory.Level,
        NavigationCategory.Module
      ])
      .subscribe((navDetails) => {
        this.navigationDetails = navDetails;
        const oldLevel = this.level;
        this.level = navDetails.level;
        this.isVisible = this.modulesWithTimezone.includes(navDetails.module);
        if (this.level && this.level !== 'portfolio') {
          const selectedPark = navDetails.selectedParkId || navDetails.selectedParksIds[0];
          const parkTimezone = this.parkTimezoneList.find(p => p._id === selectedPark).timezone;
          this.turbineTimezone = parkTimezone
        }
        this.options = this.getTimezoneOptions();
        const enterOrExitDisabledModule = (this.disabledModules.includes(navDetails.module) ||this.disabledModules.includes(this.module));
        this.isDisabled = this.disabledModules.includes(navDetails.module);
        if(!enterOrExitDisabledModule && (navDetails.selectedParkId === this.parkId && navDetails.level !== "portfolio" && oldLevel!=="portfolio" )) return
        this.module = navDetails.module;
        this.parkId = navDetails.selectedParkId;
        this.updateTZ(this.level);
      });
    this.navigationService.getIsInNewPlanEvent().subscribe((isInNewPlanEvent) => { this.isDisabled = isInNewPlanEvent })
  }


  private async setAvailableTimezones() {
    const parkTimezoneList = await this.getParkTimezoneList();
    this.parkTimezoneList = parkTimezoneList;
    this.availableTimezones = [...new Set(this.parkTimezoneList.map(p => p.timezone).concat([this.userTimezone]))].sort((a, b) => a.localeCompare(b));
  }

  private updateTZ(level: string) {
    let timezone = level ? this.getSelectedTimezone(level) : (this.isVisible ? this.getTimezoneForNoLevel() : this.userTimezone);
    if (!this.availableTimezones.includes(timezone)) {
      timezone = this.userTimezone;
    }
    let newTz = timezone;
    if(this.disabledModules.includes(this.module)) {
      if (level === 'portfolio') {
        const parks = this.navigationDetails.selectedParksIds;
        let tz = this.parkTimezoneList.find(p => parks.includes(p._id))?.timezone ?? this.parkTimezoneList[0].timezone;
        if(this.module === 'budget') tz = this.getTimezoneForPortfolio();
        newTz = tz
      } else {
        newTz = this.turbineTimezone
      }
    }
    if (!this.isFirstLoad && newTz === this.timezone) return;
    const needRefresh = newTz !== this.timezone && this.isFirstLoad;

    this.setSelectedTimezone(newTz, needRefresh);
    this.options = this.getTimezoneOptions();
    this.isFirstLoad = false;
  }

  private getTimezoneOptions(): TimezoneOption[] {
    let options = [];

    const isNotPortfolio = this.level && this.level !== 'portfolio';
    const shouldCombineTimezones = isNotPortfolio && this.turbineTimezone === this.userTimezone;

    if (shouldCombineTimezones) {
      options.push({ label: this.translateService.instant('HEADER.SITE_LOCAL_TIME'), value: this.turbineTimezone || '', isSpecial: true });
    } else {
      if (isNotPortfolio) {
        options.push({ label: this.translateService.instant('HEADER.SITE_TIME'), value: this.turbineTimezone || '', isSpecial: true });
      }
      options.push({ label: this.translateService.instant('HEADER.LOCAL_TIME'), value: this.userTimezone || '', isSpecial: true });
    }
    options.push({ divider: true });

    for (let tz of this.availableTimezones) {
      options.push({ label: tz, value: tz, isSpecial: false });
    }
    
    return options;
  }

  public getTimezoneObservable(withNavigationEvents= false){
    if(withNavigationEvents) return this.timezoneWithNavigation$.asObservable();
    return this.timezone$.asObservable();
  }

  private getSelectedTimezone(level): string {
    if(!level) return this.getTimezoneForNoLevel();
    const portfolioTZPreference = this.getPortfolioTZPreference();
    const siteTZPreference = this.getSiteTZPreference();
    

    if (level === 'portfolio') return  portfolioTZPreference ??  this.userTimezone;
    return siteTZPreference ?? this.turbineTimezone

  }

  public setSelectedTimezone(timezone: string, emit:boolean, self = this): void {
    this.timezone = timezone;
    if(emit) {
      this.timezone$.next({
        get timezone(){ return self.selectedOption},
      });
    }
    this.timezoneWithNavigation$.next({
      get timezone(){ return self.selectedOption }
    });
  }

  public getBrowserTimezone(): string {
    return this.userTimezone;
  }

  public applyTzToDate(ts: number, iana?: string): Moment {
    if(iana) return moment(TimezoneService.removeOffset(ts, iana));
    if(!this.isVisible) return moment(ts);
    return moment(TimezoneService.removeOffset(ts,this.selectedOption));
  }

  public unApplyTzFromDate(ts: number, iana?: string): Moment {
    if(iana) return TimezoneService.toLocalMoment(ts, iana);
    if(!this.isVisible) return moment(ts);
    return TimezoneService.toLocalMoment(ts, this.selectedOption);
  }

  public applyTzTimesTemp(timesTemp: ITimesTemp): ITimesTemp {
    if(!this.isVisible) return timesTemp;
    return {
      startTS: this.applyTzToDate(timesTemp.startTS).valueOf(),
      endTS: this.applyTzToDate(timesTemp.endTS).valueOf(),
    };
  }

  public unApplyTzTimesTemp(timesTemp: ITimesTemp): ITimesTemp {
    if(!this.isVisible) return timesTemp;
    return {
      startTS: this.unApplyTzFromDate(timesTemp.startTS).valueOf(),
      endTS: this.unApplyTzFromDate(timesTemp.endTS).valueOf(),
    };
  }

  public getPortfolioTZPreference(): string {
    const { portfolioTimezoneWind } =  this.userPreferencesService.getPreferences();
    return  portfolioTimezoneWind  ?? null
  }

  public getSiteTZPreference(): string {
    const  { siteTimezoneWind } =  this.userPreferencesService.getPreferences();
    return siteTimezoneWind ?? null;
  }

  public getTimezoneForPark(parkId: string): string {
    const ianaTz = this.parkTimezoneList.find(p => p._id === parkId || p.name === parkId || p.code === parkId).timezone;
    return (ianaTz);
  }

  private getTimezoneForNoLevel(): string {
    return this.getSelectedTimezone('portfolio') ?? this.userTimezone;
  }

  public getTimezoneForPortfolio(): string {
    const ianaTz = this.getSelectedTimezone('portfolio');
    return (ianaTz);
  }

  public getOffsetForTimezone(tz?: string): number {
    if(!tz) return moment.tz(this.selectedOption ?? this.getBrowserTimezone()).utcOffset()/60;
    return moment.tz(tz).utcOffset()/60;
  }

  public static ianaToGMT(iana: string,timestamp?:number): string {
    iana ??= moment.tz.guess();
    timestamp ??= Date.now();
    const m = moment.tz(timestamp, iana).utcOffset()/60;
    return m>=0? `GMT+${m}` : `GMT${m}`;
  }

  public static toLocalMoment(ts: number, iana: string): Moment {
    if(!iana) return moment(ts);
    return moment(moment.tz(ts,iana).tz(moment.tz.guess(),true).valueOf())
  }

  public static removeOffset(ts: number, iana: string) : number {
    if(!iana) return ts;
    return moment(ts).tz(iana,true).valueOf() ;
  }

  private getParkTimezoneList() {
    return this.dimensionService.getParkList().then(parks => {
      return parks.map(park => {
        return {
          _id: park.id,
          timezone: park.timezone,
          name: park.name,
          code: park.code
        }
      })
    });
  }

  public getTimezoneForReport(type: DragObjectInterface) : string {
    const params = type?.params;
    if(!params) return this.userTimezone;
    if(params.timezone && params.customTimezone) return params.timezone;
    const module = type?.module;
    const isLocked = module === 'module_kpifollowup_windlog' || module ==='module_budget_windlog';
    if(isLocked) {
      if(params.aggregationLevel === 'portfolio' || params.aggregationLevel === 'country') {
        if(module === 'module_kpifollowup_windlog') return this.getTimezoneForCustomPortfolio(type.params.portfolioId);
        return this.getTimezoneForPortfolio();
      } else {
        const parkId = params.park || params.parkId;
        return this.getTimezoneForPark(parkId);
      }
    }
    if((params.park || params.parkId) && (params.aggregationLevel === 'park' || params.aggregationLevel === 'turbine')) {
      const parkId = params.park || params.parkId;
      const sitePreference = this.getSiteTZPreference();
      if(sitePreference) return sitePreference;
      return this.getTimezoneForPark(parkId);
    }
    if(params.aggregationLevel === 'portfolio') return this.getTimezoneForPortfolio();
    return this.userTimezone;

  }

  public getTimezoneForCustomPortfolio(portfolioId: string): string {
    const portfolios = this.dimensionService.getPortfolioListSync();
    const portfolio = portfolios.find(p => p.id === portfolioId) ?? portfolios.find(p => p.defaultPortfolio) ?? portfolios[0];
    const parks = this.dimensionService.getParkListSync();
    const parkId = parks.filter(park => portfolio.parks.includes(park.id))[0].id
    return this.getTimezoneForPark(parkId);
  }

  private getLastUserTimezone(): string {
    const { userTimezone } =  this.userPreferencesService.getPreferences();
    return  userTimezone;
  }

  private setIsFromReportService() {
    const authTimeToken = this.keycloakService.idTokenParsed?.auth_time;
    this.isFromReportService = !authTimeToken;
  }

  private async setUserTimezone() {
    await this.userPreferencesService.getUserPreferencesLoaded().pipe(filter(loaded => loaded === true),take(1)).toPromise();
    let currentTz = this.getBrowserTimezone();
    const savedTz = this.getLastUserTimezone();
    this.userTimezone = this.isFromReportService ? (savedTz ??  this.parkTimezoneList[0]?.timezone) : currentTz;
    if(savedTz === currentTz) return;
    if(this.isFromReportService && savedTz) return;
    if(this.isFromReportService && !savedTz) {
      currentTz = this.parkTimezoneList[0]?.timezone;
    }
   return this.userPreferencesService.updateTimezonePreferences({ userTimezone: currentTz });
  }
}

